import PropTypes from "prop-types";
import styles from "./Badge.module.css";

/**
 * Badge/Label component following project's label pattern.
 * Uses project color palette (green-light default, plus yellow/melon/coral accents).
 * Used for roles, statuses, types, labels, and tags.
 *
 * @example
 * // Default green badge (1.5rem font, used in DisplayPlan labels)
 * <Badge>Kompetansemål</Badge>
 * <Badge color="green">Vurdering</Badge>
 *
 * @example
 * // Small size with colors (1rem font, for inline usage in Settings)
 * <Badge size="sm" color="green">Admin</Badge>
 * <Badge size="sm" color="yellow">Pending</Badge>
 * <Badge size="sm" color="melon">Warning</Badge>
 * <Badge size="sm" color="coral">Important</Badge>
 *
 * @example
 * // Status indicators
 * <Badge size="sm" color="green">✓ Connected</Badge>
 * <Badge size="sm" color="yellow">⚠ Review</Badge>
 *
 * @example
 * // Interactive badge (renders as button)
 * <Badge size="sm" color="yellow" onClick={handleClick}>Click me</Badge>
 */
const Badge = ({
  children,
  size = "sm",
  color = "green",
  className = "",
  onClick,
  ...props
}) => {
  const sizeClass = size === "sm" ? styles.sm : styles.normal;
  const colorClass = styles[color] || styles.green;
  const combinedClassName = `${styles.badge} ${sizeClass} ${colorClass} ${className}`.trim();

  // Render as button if onClick is provided, otherwise render as span
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={combinedClassName}
        {...props}
      >
        {children}
      </button>
    );
  }

  return <span {...props} className={combinedClassName}>{children}</span>;
};

Badge.propTypes = {
  children: PropTypes.node.isRequired,
  size: PropTypes.oneOf(["normal", "sm"]),
  color: PropTypes.oneOf(["green", "yellow", "melon", "coral"]),
  className: PropTypes.string,
  onClick: PropTypes.func,
};

export default Badge;
